import { Eye, EyeOff } from "lucide-react";
import {
  type ButtonHTMLAttributes,
  cloneElement,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  useId,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "../api/client.js";

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: ReactElement<{ id?: string }>;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm text-ink-2">
        {label}
      </label>
      {cloneElement(children, { id })}
      {hint ? <span className="mt-1 block text-xs text-ink-3">{hint}</span> : null}
    </div>
  );
}

const inputClass =
  "h-11 w-full rounded-lg border border-rule bg-surface px-3 text-sm text-ink outline-none transition-shadow focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:opacity-60";

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  if (props.type === "password") return <PasswordInput {...props} />;
  return <input {...props} className={`${inputClass} ${props.className ?? ""}`} />;
}

/** A password field with an eye button that shows what was typed, as Google's sign-in does. */
function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const label = visible ? t("common.hidePassword") : t("common.showPassword");
  return (
    <div className="relative">
      <input
        {...props}
        type={visible ? "text" : "password"}
        // Typed passwords are never corrected or stored as plain text by the browser.
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        className={`${inputClass} pr-11 ${props.className ?? ""}`}
      />
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-pressed={visible}
        disabled={props.disabled}
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-1 my-auto flex size-9 items-center justify-center rounded-full text-ink-3 hover:bg-ink/5 hover:text-ink"
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputClass} ${props.className ?? ""}`} />;
}

export function Button({
  variant = "primary",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "danger" }) {
  const styles = {
    primary: "bg-accent text-white hover:shadow-1",
    secondary: "border border-ink-3/40 bg-surface text-accent hover:bg-accent-soft/60",
    danger: "border border-danger/40 bg-surface text-danger hover:bg-danger-soft",
  }[variant];
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60 ${styles} ${props.className ?? ""}`}
    />
  );
}

/** Renders an API or unexpected error in the user's language; error codes map to messages. */
export function ErrorNotice({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  let message = t("common.errorGeneric");
  if (error instanceof ApiRequestError) {
    const key = `errors.${error.code}`;
    message = t(key, { defaultValue: error.message });
  }
  return (
    <div role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
      {message}
    </div>
  );
}

export function Card({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-rule bg-surface p-5 md:p-6">
      <h2 className="mb-4 text-base font-medium text-ink">{title}</h2>
      <div className="flex flex-col gap-4">{children}</div>
      {footer ? (
        <div className="mt-4 border-t border-rule pt-4 text-sm text-ink-2">{footer}</div>
      ) : null}
    </section>
  );
}

export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-rule bg-surface p-6 md:p-10">
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <img src="/icon.svg" alt="" className="size-8" />
              <span className="text-lg text-ink-2">{t("app.name")}</span>
            </div>
            <h1 className="mt-5 text-[28px] leading-tight text-ink">{title}</h1>
            {subtitle ? <p className="mt-2 text-sm text-ink-2">{subtitle}</p> : null}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
